/**
 * SearchPlaybackPanel — a small docked recording player for one search result.
 *
 * "This event, on this camera, at this moment — show me the footage." The panel opens over the
 * results grid rather than navigating away, so an operator can play one hit, dismiss it and try
 * the next without losing their query or their scroll position.
 *
 * ─── One player, both dashboards ───
 * The media comes from `RecordingPlaybackPlayer`, the shared HLS player — which carries the
 * chunk-availability handling that matters here: chunks are transcoded ON DEMAND, so a first
 * request can take a minute, a scrub cancels transcodes, and retention deletes segments under a
 * playing timeline. All of that looks like "a chunk 404'd", and retrying forever produces a
 * permanent "Buffering…" with no explanation.
 *
 * This panel owns the transport (play/pause, seek, rate) by driving the `<video>` element through
 * a ref it passes down. The transport needs no knowledge of how the media got there, so it works
 * identically wherever the panel is used.
 *
 * It opens PLAYING, and the picture itself is a play/pause target as well as the transport bar —
 * an operator scanning results wants the footage moving the moment it appears, and wants to stop
 * it where they are looking, not by finding a button underneath.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  ArrowTopRightOnSquareIcon, PauseIcon, PlayIcon, VideoCameraSlashIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import PlaybackTransport from '../recordings/PlaybackTransport';
import RecordingPlaybackPlayer from '../recordings/RecordingPlaybackPlayer';

/** Speeds the transport steps through. Mirrors the recordings page so the controls feel the same. */
const RATES = [0.25, 0.5, 1, 2, 4, 8];

export interface SearchPlaybackPanelProps {
  /** Heading — usually the camera name. */
  title: string;
  /** Sub-heading — the event's own description and time. */
  subtitle?: string;
  /** The HLS playlist for the window, from `api.search.playbackUrl`. Null = nothing to play. */
  src: string | null;
  /** Seconds of footage before the event, for the "starts at" hint. */
  leadSeconds: number;
  /** Longer timeouts, for a stream crossing the cloud's WebSocket tunnel. */
  tunnelled?: boolean;
  /** Bearer token for the playlist/segment fetches, read per request. See the player. */
  getAuthToken?: () => string | null;
  /** Open the full recordings page at this moment — the escape hatch to real review tooling. */
  onOpenFull?: () => void;
  onClose: () => void;
}

export default function SearchPlaybackPanel({
  title,
  subtitle,
  src,
  leadSeconds,
  tunnelled = false,
  getAuthToken,
  onOpenFull,
  onClose,
}: SearchPlaybackPanelProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  /** OBSERVED: what the element is actually doing. Drives the icons. */
  const [isPlaying, setIsPlaying] = useState(false);
  /**
   * INTENT: whether the operator wants this playing. Drives autoplay.
   *
   * Deliberately NOT the same state as `isPlaying`, and the panel used to pass that one to the
   * player instead — which could not work. `isPlaying` starts false and only becomes true when
   * the element fires `play`; the player only calls `play()` when this flag is already true. So
   * each waited on the other and the panel opened paused, every time, however many times you
   * clicked Playback.
   *
   * Starting at `true` is what makes opening a result play it. Pausing clears it, so a
   * mid-stream reload does not override a deliberate pause; a NEW result sets it again below.
   */
  const [wantPlaying, setWantPlaying] = useState(true);
  const [rate, setRate] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(0);
  /**
   * Whether there is a decoded frame to act on yet.
   *
   * Gates the click-to-play badge only. The player centres its own "Buffering…" notice, and the
   * badge would land on top of it during the first load — two overlapping messages where the
   * honest one is "still fetching". On-demand transcoding makes that first wait long enough to
   * see, so it is worth suppressing rather than letting them collide.
   */
  const [ready, setReady] = useState(false);

  // ── Keep our state in step with the element, whoever changed it ──
  //
  // hls.js and the browser both pause and resume on their own (buffer stalls, segment
  // boundaries, the user hitting the native controls). Mirroring the element's events rather
  // than assuming our button is the only cause is what stops the icon saying "pause" over a
  // stopped video.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onTime = () => {
      setElapsed(v.currentTime || 0);
      if (Number.isFinite(v.duration)) setDuration(v.duration);
    };
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('durationchange', onTime);
    return () => {
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('durationchange', onTime);
    };
  }, [src]);

  // A different result means a fresh intent to watch: opening the next hit should play it even
  // if the previous one was left paused.
  useEffect(() => { setWantPlaying(true); setReady(false); }, [src]);

  // Applied as an effect rather than inside the click handler: the element is replaced whenever
  // `src` changes, and a fresh element defaults back to 1× — so the chosen rate has to be
  // re-asserted, not just set once.
  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = rate;
  }, [rate, src]);

  // Escape closes. A panel that overlays content and can only be dismissed by hitting a small
  // button is the kind of thing people end up reloading the page to escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    // Intent is recorded from the CLICK, not from the resulting element event: if the play()
    // below is refused by autoplay policy no `play` event ever fires, and reading intent back
    // off the element would silently discard what the operator asked for.
    if (v.paused) {
      setWantPlaying(true);
      // The promise is caught, not awaited: an autoplay rejection is a normal outcome (a browser
      // policy, not a fault), and an unhandled rejection would surface as a console error on a
      // panel that is otherwise working.
      v.play().catch(() => { /* blocked by policy; the user can press again */ });
    } else {
      setWantPlaying(false);
      v.pause();
    }
  }, []);

  const skip = useCallback((delta: number) => {
    const v = videoRef.current;
    if (!v) return;
    const max = Number.isFinite(v.duration) ? v.duration : Infinity;
    v.currentTime = Math.max(0, Math.min(max, (v.currentTime || 0) + delta));
  }, []);

  const stepRate = useCallback((dir: 1 | -1) => {
    setRate((cur) => {
      const i = RATES.indexOf(cur);
      const next = i === -1 ? 1 : Math.max(0, Math.min(RATES.length - 1, i + dir));
      return RATES[next];
    });
  }, []);

  const slower = RATES[Math.max(0, RATES.indexOf(rate) - 1)];

  /**
   * Where the playhead sits relative to the EVENT, not the clip.
   *
   * The window starts before the event so there is context leading up to it, which means clip
   * time and event time are not the same thing. Showing "-8s / +12s" answers the question the
   * operator actually has — am I before or after the moment I searched for.
   */
  const offset = elapsed - leadSeconds;
  const offsetLabel = `${offset >= 0 ? '+' : '−'}${Math.abs(offset).toFixed(0)}s`;

  return (
    <div
      className="fixed bottom-4 right-4 z-50 w-[26rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
      role="dialog"
      aria-label={`Playback — ${title}`}
    >
      <div className="flex items-start gap-2 border-b border-border px-3 py-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-foreground">{title}</div>
          {subtitle && <div className="truncate text-[11px] text-muted-foreground">{subtitle}</div>}
        </div>
        {onOpenFull && (
          <button
            type="button"
            onClick={onOpenFull}
            title="Open in Recordings for the full timeline"
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close playback"
          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="relative aspect-video bg-black">
        {src && !error && (
          <RecordingPlaybackPlayer
            src={src}
            videoRef={videoRef}
            autoPlay
            tunnelled={tunnelled}
            getAuthToken={getAuthToken}
            playbackRate={rate}
            wantPlaying={wantPlaying}
            onCanPlay={() => setReady(true)}
            onError={setError}
          />
        )}
        {src && !error && ready && (
          /* Click the picture to play or pause, the way every video behaves. The transport
             below stays the precise control (skip, rate); this is the one an operator reaches
             for without looking. Transparent while playing so the player's own "Buffering…"
             notice still reads through it, and the badge fades in on hover to advertise that
             the picture is clickable at all. */
          <button
            type="button"
            onClick={togglePlay}
            aria-label={isPlaying ? 'Pause' : 'Play'}
            className="group absolute inset-0 flex items-center justify-center focus:outline-none"
          >
            <span
              className={`rounded-full bg-black/55 p-3 text-white transition-opacity duration-150
                ${isPlaying ? 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100' : 'opacity-100'}`}
            >
              {isPlaying ? <PauseIcon className="h-6 w-6" /> : <PlayIcon className="h-6 w-6" />}
            </span>
          </button>
        )}
        {(!src || error) && (
          /* Two different "nothing to play" cases, and the difference matters to the operator.
             No URL at all: recording was off, or the host cannot resolve one. An error: the
             player tried and the server told it why — retention, a failed transcode, a
             still-running one. The player's message is more specific than anything this panel
             could guess, so it is shown verbatim. */
          <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
            <VideoCameraSlashIcon className="h-7 w-7 text-muted-foreground" />
            <p className="text-xs text-muted-foreground">
              {error || 'No recording available for this moment — recording may have been off on '
                + 'this camera, or the footage has passed its retention window.'}
            </p>
            {error && (
              <button
                type="button"
                onClick={() => setError(null)}
                className="mt-1 rounded-md border border-border px-2 py-1 text-[11px] text-foreground hover:bg-muted"
              >
                Try again
              </button>
            )}
          </div>
        )}
      </div>

      {src && !error && (
        <div className="flex flex-col gap-1.5 px-3 py-2">
          <div className="flex items-center justify-between font-mono text-[10px] text-muted-foreground">
            <span title="Playhead position relative to the event">
              event {offsetLabel}
            </span>
            <span>
              {formatClock(elapsed)}
              {duration > 0 ? ` / ${formatClock(duration)}` : ''}
            </span>
          </div>
          <PlaybackTransport
            hasStream
            isPlaying={isPlaying}
            onPlayPause={togglePlay}
            onSkip={skip}
            playbackRate={rate}
            onSpeedUp={() => stepRate(1)}
            onSlowDown={() => stepRate(-1)}
            slowerSpeed={slower}
          />
        </div>
      )}
    </div>
  );
}

function formatClock(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}
